import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Subscription terms shown at checkout. The text comes from the admin-editable policies (GET /terms/subscription);
 * the version id pins exactly what the customer accepted, and the server records version + time and rejects a
 * purchase made without it (or with an out-of-date version).
 */
export function useTermsAcceptance() {
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/terms/subscription", { params: { _: Date.now() } });
      setBundle((prev) => { if (prev && prev.version !== data.version) setAccepted(false); return data; });
      setError("");
    } catch { setError("Could not load the terms. Please check your connection."); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Spread into a request body.
  const payload = { terms_accepted: accepted, terms_version: bundle?.version };
  return { bundle, error, accepted, setAccepted, reload: load, payload, ready: !!bundle && accepted };
}

export default function TermsAcceptance({ terms, className = "" }) {
  const { bundle, error, accepted, setAccepted } = terms;
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState("");
  const policies = bundle?.policies || [];
  const current = policies.find((p) => p.slug === slug) || policies[0];

  // One compact line on the page; the full terms open in a popup with a single dropdown.
  return (
    <div className={`rounded-2xl border border-border/70 bg-background/60 p-3.5 ${className}`} data-testid="terms-acceptance">
      {error && <p role="alert" className="mb-2 text-xs text-destructive">{error} <button type="button" className="underline" onClick={terms.reload}>Retry</button></p>}
      <label className="flex cursor-pointer items-start gap-2.5 text-sm min-h-9">
        <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0" checked={accepted} disabled={!bundle} onChange={(e) => setAccepted(e.target.checked)} data-testid="terms-checkbox" />
        <span>
          I have read and agree to the{" "}
          <button type="button" data-testid="terms-open" onClick={(e) => { e.preventDefault(); setOpen(true); }} className="font-semibold text-primary underline underline-offset-2">Terms &amp; Conditions</button>
          {bundle ? <span className="text-muted-foreground"> (version {bundle.version})</span> : <span className="text-muted-foreground"> (loading…)</span>}.
        </span>
      </label>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="terms-dialog" className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle>Terms for your subscription</DialogTitle>
            <DialogDescription>Choose a policy from the list to read it.</DialogDescription>
          </DialogHeader>
          {current && (
            <div className="space-y-3">
              <Select value={current.slug} onValueChange={setSlug}>
                <SelectTrigger data-testid="terms-select" className="min-h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{policies.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
              <div className="max-h-[50vh] overflow-y-auto whitespace-pre-line rounded-xl border border-border/60 bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">{current.body}</div>
            </div>
          )}
          <button type="button" data-testid="terms-accept" onClick={() => { setAccepted(true); setOpen(false); }}
            className="w-full min-h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
            I agree to the Terms &amp; Conditions
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
